import { useState } from "react";
import type { CampaignReportResponse, InsightCallout } from "../types";
import { streamInsights } from "../api/insightApi";

type InsightsMetadata = {
  internalModel?: string;
  externalModel?: string;
  internalGenerationTime?: number;
  externalGenerationTime?: number;
};

type PerspectiveLoadingState = {
  internal: boolean;
  external: boolean;
};

type InsightProgressState = Partial<Record<"internal" | "external", string>>;

export function useAiInsights(campaignData: CampaignReportResponse) {
  const [internalInsights, setInternalInsights] = useState<InsightCallout[]>(
    [],
  );
  const [externalInsights, setExternalInsights] = useState<InsightCallout[]>(
    [],
  );
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [insightsMetadata, setInsightsMetadata] = useState<InsightsMetadata>(
    {},
  );
  const [insightProgress, setInsightProgress] = useState<InsightProgressState>(
    {},
  );
  const [perspectiveLoading, setPerspectiveLoading] =
    useState<PerspectiveLoadingState>({
      internal: false,
      external: false,
    });

  const resetInsights = () => {
    setInsightsMetadata({});
    setInsightsError(null);
    setInsightProgress({});
    setPerspectiveLoading({ internal: false, external: false });
  };

  const dismissInsights = () => {
    setInternalInsights([]);
    setExternalInsights([]);
    resetInsights();
  };

  const markInsightAdded = (
    insight: InsightCallout,
    perspective: "internal" | "external",
  ) => {
    if (perspective === "internal") {
      setInternalInsights((currentInsights) =>
        currentInsights.map((currentInsight) =>
          currentInsight.id === insight.id
            ? { ...currentInsight, is_accepted: false }
            : currentInsight,
        ),
      );
      return;
    }

    setExternalInsights((currentInsights) =>
      currentInsights.map((currentInsight) =>
        currentInsight.id === insight.id
          ? { ...currentInsight, is_accepted: false }
          : currentInsight,
      ),
    );
  };

  const generateInsights = async () => {
    setIsGeneratingInsights(true);
    resetInsights();
    setPerspectiveLoading({ internal: true, external: true });

    const runPerspectiveAnalysis = (perspective: "internal" | "external") => {
      return streamInsights(
        {
          report_data: campaignData,
          model: "gpt-5-mini-2025-08-07",
          use_code_interpreter: false,
          max_insights: 5,
          perspective,
        },
        (_stage, message) => {
          setInsightProgress((prev) => ({
            ...prev,
            [perspective]: message,
          }));
        },
      ).finally(() => {
        setPerspectiveLoading((prev) => ({
          ...prev,
          [perspective]: false,
        }));
        setInsightProgress((prev) => {
          const next = { ...prev };
          delete next[perspective];
          return next;
        });
      });
    };

    const errors: string[] = [];

    const internalPromise = runPerspectiveAnalysis("internal")
      .then((result) => {
        setInternalInsights(result.insights);
        setInsightsMetadata((prev) => ({
          ...prev,
          internalModel: result.model_used,
          internalGenerationTime: result.generation_time_seconds,
        }));
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Internal insights failed";
        errors.push(`Internal perspective: ${message}`);
        setInternalInsights([]);
        console.error("Internal insights generation error:", error);
      });

    const externalPromise = runPerspectiveAnalysis("external")
      .then((result) => {
        setExternalInsights(result.insights);
        setInsightsMetadata((prev) => ({
          ...prev,
          externalModel: result.model_used,
          externalGenerationTime: result.generation_time_seconds,
        }));
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : "External insights failed";
        errors.push(`External perspective: ${message}`);
        setExternalInsights([]);
        console.error("External insights generation error:", error);
      });

    await Promise.allSettled([internalPromise, externalPromise]);

    if (errors.length > 0) {
      setInsightsError(errors.join(" | "));
    }

    setIsGeneratingInsights(false);
    setPerspectiveLoading({ internal: false, external: false });
    setInsightProgress({});
  };

  return {
    dismissInsights,
    externalInsights,
    generateInsights,
    insightProgress,
    insightsError,
    insightsMetadata,
    internalInsights,
    isGeneratingInsights,
    markInsightAdded,
    perspectiveLoading,
    setExternalInsights,
    setInternalInsights,
  };
}
